export const _translations = {
	legacy: false,
	locale: 'de',
	messages: {
		en: {
			admin: {
				featureFlags: {
					title: 'Feature flags',
					subtitle: 'Switches for features under test, stored in this browser only',
					note: 'Stored per browser (localStorage), not per user. Open charts redraw right after a change; other tabs follow on their next redraw.',
					on: 'On',
					off: 'Off',
					reset: 'Reset all',
					defaultOff: 'default: off',
					defaultOn: 'default: on',
					flags: {
						provisionalBucket: {
							title: 'Show the running window as provisional',
							description:
								'Chart points sit at the end of their time window. Off (default): the window that is still running is left out of line, band and info box. On: it is shown as a hollow point at the current time, joined to the last completed point by a dashed line, without min/max band and marked "in progress · so far" in the info box. Exports contain completed windows only either way.',
						},
					},
				},
				traffic: {
					title: 'Traffic',
					subtitle: 'Who calls which endpoint, and when',
					tabs: { server: 'Server', traffic: 'Traffic', calls: 'Single requests' },
					generated: 'as of',
					refresh: 'Refresh',
					autoRefresh: 'Auto-refresh',
					range: { day: '24 h', week: '7 days', month: '30 days' },
					actors: { all: 'All', human: 'Humans', device: 'Devices', bot: 'Bots', anon: 'Anonymous' },
					now: {
						title: 'Right now',
						lastMinute: 'requests in the last full minute',
						avg5: 'avg/min over 5 min',
						avg15: 'avg/min over 15 min',
						perMinute: 'requests per minute',
					},
					window: { title: 'Window', total: 'requests in the last {days} day(s)', errors: 'Errors (4xx/5xx)' },
					server: {
						title: 'Server',
						cpu: 'CPU',
						load: 'load',
						process: 'API process',
						memory: 'RAM',
						disk: 'Disk',
						uptime: 'Uptime process / server',
					},
					heatmap: {
						title: 'Requests by day and hour',
						hint: 'One row per day, one column per hour. A click on a route below shows that route alone.',
						routeHint: 'Only this route. The actor filter does not apply to a single route.',
						clear: 'Clear route filter',
					},
					routes: { title: 'Top routes', route: 'Route', hits: 'Requests', share: 'Share', errors: 'Errors', ms: 'ms min · avg · max', hours: 'By hour' },
					actorsTable: { title: 'Top actors', label: 'Name', cls: 'Kind', hits: 'Requests' },
					hidden: {
						title: 'Hidden',
						hide: 'Hide route',
						show: 'Show route',
						showAll: 'Show all',
						hideAll: 'Hide all',
						summary: '{n} route(s) hidden · {hits} requests not counted',
						heatBadge: 'without {n} route(s)',
						actorsHint: 'Actors count all routes, hidden ones included.',
					},
					live: { ws: 'Live', poll: 'Polled every 5 s', off: 'Paused' },
					empty: 'No data yet. Counting starts when the server starts.',
					calls: {
						hint: 'Every single request of the last 30 days: which routes, who, from which IP, and what was sent (bodies shortened, passwords and tokens replaced by ***). The window selected in the graph filters everything below; a click on a route, actor or IP narrows it further.',
						pick: 'Select a window by dragging in the graph, or use a preset or the from / to fields.',
						clearSelection: 'Clear selection',
						graph: {
							title: 'Requests over time',
							step: 'per {step}',
							errors: 'Errors (4xx/5xx)',
							errorsShort: 'Errors',
							reset: 'Reset (30 days)',
							hint: 'Mouse wheel or pinch zooms in and out, sideways scrolling (or Shift + wheel) moves the view, dragging with the mouse selects a window.',
						},
						presets: { m15: 'Last 15 min', h1: '1 h', h6: '6 h', h24: '24 h' },
						from: 'From',
						to: 'To (incl. that minute)',
						calls: 'requests',
						excluded: '{n} requests of hidden routes not shown',
						dropped: '{n} requests not logged, daily limit of {cap} reached',
						clearFilter: 'Remove filter',
						clearAll: 'Remove all filters',
						routes: 'Routes',
						actors: 'Actors',
						ips: 'IP addresses',
						ipsOverflow: '{n} requests from further IPs not listed',
						unmatched: 'no route',
						none: 'No requests in this window.',
						list: 'Requests',
						searchPlaceholder: 'Search path, name, IP …',
						shown: '{n} of {total} shown',
						more: 'Load more',
						status: { all: 'All statuses', '2xx': '2xx success', '3xx': '3xx redirect', '4xx': '4xx client error', '5xx': '5xx server error', err: 'Errors (4xx/5xx)' },
						cols: {
							method: 'Method',
							route: 'Route',
							count: 'Requests',
							errors: 'Errors',
							msMin: 'ms min',
							msAvg: 'ms avg',
							msMax: 'ms max',
							time: 'Time',
							path: 'Path',
							status: 'Status',
							actor: 'Actor',
						},
						detail: {
							time: 'Time',
							path: 'Path',
							route: 'Route',
							actor: 'Actor',
							ua: 'User agent',
							body: 'Body',
							onlyActor: 'only this actor',
							onlyIp: 'only this IP',
						},
						body: {
							bytes: 'bytes',
							kind: {
								json: 'complete',
								text: 'complete',
								shape: 'structure only',
								head: 'first 240 characters',
								keys: 'top-level keys only',
								size: 'size only',
							},
						},
					},
					serverLoad: {
						title: 'Server load',
						hint: 'Sampled every 10 s, stored per minute as min · avg · max. Mouse wheel or pinch zooms in and out, sideways scrolling (or Shift + wheel) moves the view, dragging across a chart zooms into that range; hovering shows the values of that moment in all charts.',
						range: { h1: '1 h', h6: '6 h', h24: '24 h', d7: '7 days', d14: '14 days', d30: '30 days' },
						resetZoom: 'Reset zoom',
						step: 'resolution {step}',
						empty: 'No samples in this window.',
						linuxOnly: 'Measured on Linux only.',
						cores: '{n} cores',
						charts: {
							cpu: 'CPU',
							load: 'Load average',
							mem: 'RAM',
							io: 'Disk I/O',
							disk: 'Disk usage',
							net: 'Bandwidth',
							proc: 'API process CPU',
							rss: 'API process memory (RSS)',
						},
						lines: {
							cpu: 'CPU',
							load1: '1 min',
							load5: '5 min',
							load15: '15 min',
							mem: 'Used',
							disk: 'Used',
							ioRead: 'Read',
							ioWrite: 'Write',
							netRx: 'Inbound',
							netTx: 'Outbound',
							proc: 'CPU',
							rss: 'RSS',
						},
					},
				},
				yoctolog: {
					title: 'Yocto-Logs',
					subtitle: 'Hub payloads per hub and day as JSONL',
					generated: 'as of',
					refresh: 'Refresh',
					explain: {
						what: 'Every hub payload is stored as a single file under _YOCTOLOG. Each finished day of a hub is combined into one JSONL file, one line per file, sorted by receive time; error files of the same day are included with status "err".',
						safe: 'Nothing is deleted: the single files stay unchanged. Each day file is checked against the single files after conversion.',
						when: 'Runs nightly at 03:15, deliberately slowly with pauses. Today is not converted while files are still arriving.',
					},
					run: {
						title: 'Run',
						running: 'Running',
						idle: 'Idle',
						since: 'since',
						last: 'Last run',
						never: 'No run so far.',
					},
					mode: { convert: 'Convert', verify: 'Verify only' },
					trigger: { cron: 'nightly', admin: 'manual' },
					action: { convert: 'Converting', verify: 'Verifying', check: 'Counting' },
					result: { done: 'Done', budget: 'Time limit reached, continued next run', error: 'Failed', interrupted: 'Interrupted' },
					counters: {
						days: 'Days',
						converted: 'Days converted',
						verified: 'Days verified',
						unchanged: 'Days unchanged',
						files: 'Files read',
						written: 'Written',
						mismatches: 'Mismatches / errors',
					},
					totals: {
						title: 'Totals',
						hubs: 'Hubs',
						days: 'Days (without today)',
						sourceFiles: 'Single files ok / err',
						lines: 'Lines ok / err',
						size: 'Size JSONL',
					},
					actions: {
						title: 'All hubs',
						hint: 'Convert: days not yet converted, with mismatches or changed file counts. Verify only: every converted day is compared with the single files. A single day can be converted again in its row.',
						convert: 'Convert again',
						verify: 'Verify only',
					},
					filter: { hub: 'Search hub', all: 'All states' },
					status: { complete: 'complete', mismatch: 'mismatch', pending: 'pending', today: 'today' },
					table: {
						title: 'Hubs and days',
						hubDate: 'Hub / day',
						source: 'Files ok / err',
						lines: 'Lines ok / err',
						size: 'Size',
						status: 'State',
						missingExtra: 'Missing / extra',
						checked: 'Verified',
						daysShort: 'days',
						expandAll: 'Expand all',
						collapseAll: 'Collapse all',
						showAll: 'Show all {n} days',
						empty: 'No entries.',
						sourceGone: 'source gone',
						sourceGoneHint: 'The single files of this day no longer exist. The JSONL file is the only copy.',
						rawHint: 'Lines stored as text because the file was empty or not valid JSON',
						readErrors: '{n} unreadable',
						todayHint: 'still being written',
					},
					toasts: { started: 'Run started' },
				},
				header: {
					title: 'Admin',
				},
				recalcAll: {
					title: 'Recalculate modules',
					badge: 'Server-side',
					description:
						'All modules of all hubs are recalculated one after another, like the single "Recalculate" button, with a {pause} s pause in between. The run keeps going on the server when this page is closed.',
					start: 'Recalculate all modules',
					cancel: 'Cancel',
					cancelling: 'Stopping after the current module …',
					confirm: 'Recalculate all {count} modules one by one? This takes a while and loads the server.',
					confirmUnknown: 'Recalculate all modules one by one? This takes a while and loads the server.',
					never: 'No run yet',
					running: 'Running',
					finished: 'Finished',
					cancelled: 'Cancelled',
					interrupted: 'Interrupted by a server restart',
					total: 'Total',
					done: 'Done',
					failed: 'Failed',
					current: 'Current',
					startedAt: 'Started',
					finishedAt: 'Ended',
					elapsed: 'Elapsed',
					startedBy: 'by {name}',
					showTable: 'Modules ({count})',
					loadError: 'Status could not be loaded: {msg}',
					headers: {
						hub: 'Hub',
						module: 'Module',
						state: 'State',
						duration: 'Duration',
						lastCalculated: 'Last recalculated',
					},
					states: { waiting: 'waiting', running: 'running', done: 'done', failed: 'error' },
					button: {
						cancelHint: 'Running — click to stop after the current item',
						cancelConfirm: 'Stop the run after the current item?',
						last: 'Last run: {date} · {done} done · {failed} failed',
					},
					graphs: {
						title: 'Recalculate graphs',
						description:
							'All custom graphs are recalculated one after another in list order, like the single "Recalculate" button, with a {pause} s pause in between. The run keeps going on the server when this page is closed.',
						start: 'Recalculate all graphs',
						cancelling: 'Stopping after the current graph …',
						confirm: 'Recalculate all {count} graphs one by one? This takes a while and loads the server.',
						confirmUnknown: 'Recalculate all graphs one by one? This takes a while and loads the server.',
						showTable: 'Graphs ({count})',
						item: 'Graph',
					},
				},
				systemActions: {
					title: 'System actions',
					badge: 'Quick tools',
					description:
						'Execute maintenance and cache operations safely. Results appear in the log above.',
					buttons: {
						integrityCheck: 'Integrity check',
						cleanStorage: 'Clean storage',
						listSpace: 'List space usage',
						breakBinaries: 'Break binaries',
						refreshCustomGraphs: 'Refresh custom graphs',
						refreshModulesCache: 'Refresh modules cache',
						analyzeAlertData: 'Analyze alert data',
						sendWarningEmail: 'Send warning email',
					},
					toasts: {
						integrityCheckSuccess: 'Integrity check completed successfully.',
						binariesBroken: 'Binaries broken successfully.',
						commappZipCreated: 'CommApp zip created successfully.',
						customGraphsRefreshed: 'Custom graphs refreshed successfully.',
						modulesCacheRefreshed: 'Module cache refreshed successfully.',
						storageCleaned: 'Storage cleaned successfully.',
						spaceUsageListed: 'Space usage listed successfully.',
						alertDataAnalyzed: 'Alert data analyzed successfully.',
						warningEmailSent: 'Warning email sent successfully.',
					},
				},
				dataWindow: {
					title: 'Data window',
					badge: 'Config',
					description: 'Choose the CommApp archive slice before running creation or analysis.',
					fields: {
						pcName: 'pcName',
						year: 'Year',
						month: 'Month',
						part: 'Part',
					},
					actions: {
						createCommappZip: 'Create CommApp zip',
					},
				},
				operationLog: {
					title: 'Operation log',
					badge: 'Results',
					description: 'Recent responses from admin utilities.',
				},
				ipcalls: {
					table: {
						ip: 'IP',
						country: 'Country',
						callCount: 'Call count',
						created: 'Created',
						modified: 'Modified',
						approved: 'Approved',
					},
					actions: {
						allow: 'allow',
						block: 'block',
						confirmTitle: 'Are you sure?',
						allowText: 'Allow IP {ip}',
						blockText: 'Block IP {ip}',
					},
					toasts: {
						ipAllowed: 'IP allowed',
						ipBlocked: 'IP blocked',
						listLoaded: 'IP list loaded',
					},
				},
				yocto: {
					status: {
						online: 'online',
						offline: 'offline',
						notConnected: 'Not connected to websockets - reload page',
					},
					sections: {
						futureSettings: 'Future Settings',
						onlineSettings: 'Online Settings',
						currentSensors: 'Current Sensors',
						devices: 'Devices',
						sensors: 'Sensors (yellowPages)',
						devLogs: 'dev logs',
					},
					labels: {
						logicalName: 'Logical Name',
						luminosity: 'Luminosity',
						goToSleepAfter: 'Go To Sleep After',
						wakeUpSchedule1: 'WakeUpSchedule 1',
						wakeUpSchedule2: 'WakeUpSchedule 2',
						sendModuleToSleep: 'Send Module To Sleep',
						disconnectWebsocket: 'Disconnect Websocket',
					},
					buttons: {
						setSchedule: 'Set Schedule',
						sendFutureSettings: 'Send Future Settings',
						sendSleep: 'Send Sleep',
						disconnect: 'disconnect',
						toggleDevLogs: 'dev logs',
					},
				},
			},
		},
		de: {
			admin: {
				featureFlags: {
					title: 'Feature-Flags',
					subtitle: 'Schalter für Funktionen in Erprobung, nur in diesem Browser gespeichert',
					note: 'Gespeichert je Browser (localStorage), nicht je Benutzer. Offene Charts werden nach einer Änderung sofort neu gezeichnet; andere Tabs ziehen beim nächsten Neuzeichnen nach.',
					on: 'An',
					off: 'Aus',
					reset: 'Alle zurücksetzen',
					defaultOff: 'Standard: aus',
					defaultOn: 'Standard: an',
					flags: {
						provisionalBucket: {
							title: 'Laufenden Abschnitt als vorläufig anzeigen',
							description:
								'Chart-Punkte liegen am Ende ihres Zeitabschnitts. Aus (Standard): Der noch laufende Abschnitt wird in Linie, Fläche und Infobox weggelassen. An: Er wird als hohler Punkt zur aktuellen Uhrzeit gezeigt, gestrichelt mit dem letzten abgeschlossenen Punkt verbunden, ohne Min/Max-Fläche und in der Infobox als „läuft · bisher“ gekennzeichnet. Exporte enthalten in beiden Fällen nur abgeschlossene Abschnitte.',
						},
					},
				},
				traffic: {
					title: 'Traffic',
					subtitle: 'Wer ruft welche Schnittstelle wann auf',
					tabs: { server: 'Server', traffic: 'Traffic', calls: 'Einzelaufrufe' },
					generated: 'Stand',
					refresh: 'Aktualisieren',
					autoRefresh: 'Auto-Aktualisierung',
					range: { day: '24 h', week: '7 Tage', month: '30 Tage' },
					actors: { all: 'Alle', human: 'Menschen', device: 'Geräte', bot: 'Bots', anon: 'Anonym' },
					now: {
						title: 'Gerade jetzt',
						lastMinute: 'Aufrufe in der letzten vollen Minute',
						avg5: 'Ø/min über 5 min',
						avg15: 'Ø/min über 15 min',
						perMinute: 'Aufrufe je Minute',
					},
					window: { title: 'Zeitraum', total: 'Aufrufe in den letzten {days} Tag(en)', errors: 'Fehler (4xx/5xx)' },
					server: {
						title: 'Server',
						cpu: 'CPU',
						load: 'Load',
						process: 'API-Prozess',
						memory: 'RAM',
						disk: 'Festplatte',
						uptime: 'Laufzeit Prozess / Server',
					},
					heatmap: {
						title: 'Aufrufe nach Tag und Stunde',
						hint: 'Eine Zeile je Tag, eine Spalte je Stunde. Ein Klick auf eine Route in der Tabelle zeigt nur diese Route.',
						routeHint: 'Nur diese Route. Der Akteur-Filter gilt für eine einzelne Route nicht.',
						clear: 'Routen-Filter aufheben',
					},
					routes: { title: 'Top-Routen', route: 'Route', hits: 'Aufrufe', share: 'Anteil', errors: 'Fehler', ms: 'ms min · Ø · max', hours: 'Stundenverlauf' },
					actorsTable: { title: 'Top-Akteure', label: 'Name', cls: 'Art', hits: 'Aufrufe' },
					hidden: {
						title: 'Ausgeblendet',
						hide: 'Route ausblenden',
						show: 'Route einblenden',
						showAll: 'Alle einblenden',
						hideAll: 'Alle ausblenden',
						summary: '{n} Route(n) ausgeblendet · {hits} Aufrufe nicht gezählt',
						heatBadge: 'ohne {n} Route(n)',
						actorsHint: 'Akteure zählen über alle Routen, auch die ausgeblendeten.',
					},
					live: { ws: 'Live', poll: 'Abfrage alle 5 s', off: 'Pausiert' },
					empty: 'Noch keine Daten. Gezählt wird ab dem Start des Servers.',
					calls: {
						hint: 'Jeder einzelne Aufruf der letzten 30 Tage: welche Routen, wer, von welcher IP und was gesendet wurde (Bodies gekürzt, Passwörter und Tokens durch *** ersetzt). Der im Diagramm ausgewählte Zeitraum filtert alles darunter; ein Klick auf Route, Akteur oder IP schränkt weiter ein.',
						pick: 'Zeitraum durch Ziehen im Diagramm auswählen, alternativ über eine Voreinstellung oder die Felder Von / Bis.',
						clearSelection: 'Auswahl aufheben',
						graph: {
							title: 'Aufrufe im Zeitverlauf',
							step: 'je {step}',
							errors: 'Fehler (4xx/5xx)',
							errorsShort: 'Fehler',
							reset: 'Zurücksetzen (30 Tage)',
							hint: 'Vergrößern und Verkleinern per Mausrad oder Zwei-Finger-Geste, Verschieben per seitlichem Scrollen (oder Umschalt + Mausrad), Auswahl eines Zeitraums durch Ziehen mit der Maus.',
						},
						presets: { m15: 'Letzte 15 min', h1: '1 h', h6: '6 h', h24: '24 h' },
						from: 'Von',
						to: 'Bis (inkl. dieser Minute)',
						calls: 'Aufrufe',
						excluded: '{n} Aufrufe ausgeblendeter Routen nicht gezeigt',
						dropped: '{n} Aufrufe nicht protokolliert, Tagesgrenze von {cap} erreicht',
						clearFilter: 'Filter entfernen',
						clearAll: 'Alle Filter entfernen',
						routes: 'Routen',
						actors: 'Akteure',
						ips: 'IP-Adressen',
						ipsOverflow: '{n} Aufrufe weiterer IPs nicht aufgelistet',
						unmatched: 'keine Route',
						none: 'Keine Aufrufe in diesem Zeitraum.',
						list: 'Aufrufe',
						searchPlaceholder: 'Suche in Pfad, Name, IP …',
						shown: '{n} von {total} angezeigt',
						more: 'Mehr laden',
						status: { all: 'Alle Status', '2xx': '2xx Erfolg', '3xx': '3xx Umleitung', '4xx': '4xx Client-Fehler', '5xx': '5xx Server-Fehler', err: 'Fehler (4xx/5xx)' },
						cols: {
							method: 'Methode',
							route: 'Route',
							count: 'Aufrufe',
							errors: 'Fehler',
							msMin: 'ms min',
							msAvg: 'ms Ø',
							msMax: 'ms max',
							time: 'Zeit',
							path: 'Pfad',
							status: 'Status',
							actor: 'Akteur',
						},
						detail: {
							time: 'Zeit',
							path: 'Pfad',
							route: 'Route',
							actor: 'Akteur',
							ua: 'User-Agent',
							body: 'Body',
							onlyActor: 'nur dieser Akteur',
							onlyIp: 'nur diese IP',
						},
						body: {
							bytes: 'Bytes',
							kind: {
								json: 'vollständig',
								text: 'vollständig',
								shape: 'nur Struktur',
								head: 'erste 240 Zeichen',
								keys: 'nur Schlüssel der obersten Ebene',
								size: 'nur Größe',
							},
						},
					},
					serverLoad: {
						title: 'Server-Auslastung',
						hint: 'Messung alle 10 s, gespeichert je Minute als Min · Ø · Max. Vergrößern und Verkleinern per Mausrad oder Zwei-Finger-Geste, Verschieben per seitlichem Scrollen (oder Umschalt + Mausrad), Vergrößern eines Bereichs durch Aufziehen mit der Maus; beim Überfahren erscheinen die Werte dieses Zeitpunkts in allen Diagrammen.',
						range: { h1: '1 h', h6: '6 h', h24: '24 h', d7: '7 Tage', d14: '14 Tage', d30: '30 Tage' },
						resetZoom: 'Zoom zurücksetzen',
						step: 'Auflösung {step}',
						empty: 'Keine Messwerte in diesem Zeitraum.',
						linuxOnly: 'Nur unter Linux gemessen.',
						cores: '{n} Kerne',
						charts: {
							cpu: 'CPU',
							load: 'Load-Durchschnitt',
							mem: 'RAM',
							io: 'Disk-I/O',
							disk: 'Festplattenbelegung',
							net: 'Bandbreite',
							proc: 'API-Prozess CPU',
							rss: 'API-Prozess Speicher (RSS)',
						},
						lines: {
							cpu: 'CPU',
							load1: '1 min',
							load5: '5 min',
							load15: '15 min',
							mem: 'Belegt',
							disk: 'Belegt',
							ioRead: 'Lesen',
							ioWrite: 'Schreiben',
							netRx: 'Eingehend',
							netTx: 'Ausgehend',
							proc: 'CPU',
							rss: 'RSS',
						},
					},
				},
				yoctolog: {
					title: 'Yocto-Logs',
					subtitle: 'Hub-Daten je Hub und Tag als JSONL',
					generated: 'Stand',
					refresh: 'Aktualisieren',
					explain: {
						what: 'Jede Hub-Übertragung wird als Einzeldatei unter _YOCTOLOG abgelegt. Jeder abgeschlossene Tag eines Hubs wird zu einer JSONL-Datei zusammengefasst, eine Zeile je Datei, sortiert nach Empfangszeit; Fehlerdateien desselben Tages werden mit Status „err“ aufgenommen.',
						safe: 'Es wird nichts gelöscht: Die Einzeldateien bleiben unverändert erhalten. Jede Tagesdatei wird nach dem Umwandeln mit den Einzeldateien abgeglichen.',
						when: 'Lauf jede Nacht um 03:15, bewusst langsam mit Pausen. Der heutige Tag wird nicht umgewandelt, solange noch Dateien eintreffen.',
					},
					run: {
						title: 'Lauf',
						running: 'Läuft',
						idle: 'Bereit',
						since: 'seit',
						last: 'Letzter Lauf',
						never: 'Bisher kein Lauf.',
					},
					mode: { convert: 'Umwandeln', verify: 'Nur prüfen' },
					trigger: { cron: 'nächtlich', admin: 'manuell' },
					action: { convert: 'Umwandlung', verify: 'Prüfung', check: 'Zählung' },
					result: { done: 'Fertig', budget: 'Zeitlimit erreicht, Fortsetzung beim nächsten Lauf', error: 'Fehlgeschlagen', interrupted: 'Abgebrochen' },
					counters: {
						days: 'Tage',
						converted: 'Tage umgewandelt',
						verified: 'Tage geprüft',
						unchanged: 'Tage unverändert',
						files: 'Dateien gelesen',
						written: 'Geschrieben',
						mismatches: 'Abweichungen / Fehler',
					},
					totals: {
						title: 'Gesamt',
						hubs: 'Hubs',
						days: 'Tage (ohne heute)',
						sourceFiles: 'Einzeldateien ok / err',
						lines: 'Zeilen ok / err',
						size: 'Größe JSONL',
					},
					actions: {
						title: 'Alle Hubs',
						hint: 'Umwandeln: Tage, die noch fehlen, abweichen oder deren Dateianzahl sich geändert hat. Nur prüfen: jeder umgewandelte Tag wird mit den Einzeldateien verglichen. Ein einzelner Tag lässt sich in seiner Zeile erneut umwandeln.',
						convert: 'Neu umwandeln',
						verify: 'Nur prüfen',
					},
					filter: { hub: 'Hub suchen', all: 'Alle Zustände' },
					status: { complete: 'vollständig', mismatch: 'abweichend', pending: 'offen', today: 'heute' },
					table: {
						title: 'Hubs und Tage',
						hubDate: 'Hub / Tag',
						source: 'Dateien ok / err',
						lines: 'Zeilen ok / err',
						size: 'Größe',
						status: 'Zustand',
						missingExtra: 'Fehlend / überzählig',
						checked: 'Geprüft',
						daysShort: 'Tage',
						expandAll: 'Alle aufklappen',
						collapseAll: 'Alle zuklappen',
						showAll: 'Alle {n} Tage anzeigen',
						empty: 'Keine Einträge.',
						sourceGone: 'Quelle entfernt',
						sourceGoneHint: 'Die Einzeldateien dieses Tages sind nicht mehr vorhanden. Die JSONL-Datei ist die einzige Kopie.',
						rawHint: 'Als Text gespeicherte Zeilen, da die Datei leer oder kein gültiges JSON war',
						readErrors: '{n} nicht lesbar',
						todayHint: 'wird noch beschrieben',
					},
					toasts: { started: 'Lauf gestartet' },
				},
				header: {
					title: 'Admin',
				},
				recalcAll: {
					title: 'Module neu berechnen',
					badge: 'Serverseitig',
					description:
						'Alle Module aller Hubs werden nacheinander neu berechnet, wie mit dem einzelnen „Neu berechnen“, mit {pause} s Pause dazwischen. Der Lauf geht auf dem Server weiter, auch wenn diese Seite geschlossen wird.',
					start: 'Alle Module neu berechnen',
					cancel: 'Abbrechen',
					cancelling: 'Abbruch nach dem aktuellen Modul …',
					confirm: 'Alle {count} Module nacheinander neu berechnen? Das dauert eine Weile und belastet den Server.',
					confirmUnknown: 'Alle Module nacheinander neu berechnen? Das dauert eine Weile und belastet den Server.',
					never: 'Noch kein Lauf',
					running: 'Läuft',
					finished: 'Abgeschlossen',
					cancelled: 'Abgebrochen',
					interrupted: 'Durch Serverneustart unterbrochen',
					total: 'Gesamt',
					done: 'Fertig',
					failed: 'Fehler',
					current: 'Aktuell',
					startedAt: 'Gestartet',
					finishedAt: 'Beendet',
					elapsed: 'Dauer',
					startedBy: 'von {name}',
					showTable: 'Module ({count})',
					loadError: 'Status nicht ladbar: {msg}',
					headers: {
						hub: 'Hub',
						module: 'Modul',
						state: 'Status',
						duration: 'Dauer',
						lastCalculated: 'Zuletzt berechnet',
					},
					states: { waiting: 'wartet', running: 'läuft', done: 'fertig', failed: 'Fehler' },
					button: {
						cancelHint: 'Läuft – ein Klick bricht nach dem aktuellen Eintrag ab',
						cancelConfirm: 'Lauf nach dem aktuellen Eintrag abbrechen?',
						last: 'Letzter Lauf: {date} · {done} fertig · {failed} Fehler',
					},
					graphs: {
						title: 'Graphen neu berechnen',
						description:
							'Alle Custom-Graphen werden in der Reihenfolge der Liste nacheinander neu berechnet, wie mit dem einzelnen „Neu berechnen“, mit {pause} s Pause dazwischen. Der Lauf geht auf dem Server weiter, auch wenn diese Seite geschlossen wird.',
						start: 'Alle Graphen neu berechnen',
						cancelling: 'Abbruch nach dem aktuellen Graphen …',
						confirm: 'Alle {count} Graphen nacheinander neu berechnen? Das dauert eine Weile und belastet den Server.',
						confirmUnknown: 'Alle Graphen nacheinander neu berechnen? Das dauert eine Weile und belastet den Server.',
						showTable: 'Graphen ({count})',
						item: 'Graph',
					},
				},
				systemActions: {
					title: 'Systemaktionen',
					badge: 'Schnellwerkzeuge',
					description:
						'Wartungs- und Cache-Operationen sicher ausführen. Ergebnisse erscheinen im obigen Protokoll.',
					buttons: {
						integrityCheck: 'Integritätsprüfung',
						cleanStorage: 'Speicher bereinigen',
						listSpace: 'Speicherverbrauch anzeigen',
						breakBinaries: 'Binaries beschädigen',
						refreshCustomGraphs: 'Benutzerdefinierte Diagramme aktualisieren',
						refreshModulesCache: 'Modul-Cache aktualisieren',
						analyzeAlertData: 'Alarmdaten analysieren',
						sendWarningEmail: 'Warn-E-Mail senden',
					},
					toasts: {
						integrityCheckSuccess: 'Integritätsprüfung erfolgreich abgeschlossen.',
						binariesBroken: 'Binaries wurden erfolgreich beschädigt.',
						commappZipCreated: 'CommApp-Zip erfolgreich erstellt.',
						customGraphsRefreshed: 'Benutzerdefinierte Diagramme aktualisiert.',
						modulesCacheRefreshed: 'Modul-Cache erfolgreich aktualisiert.',
						storageCleaned: 'Speicher erfolgreich bereinigt.',
						spaceUsageListed: 'Speicherverbrauch erfolgreich ausgegeben.',
						alertDataAnalyzed: 'Alarmdaten erfolgreich analysiert.',
						warningEmailSent: 'Warn-E-Mail erfolgreich gesendet.',
					},
				},
				dataWindow: {
					title: 'Datenfenster',
					badge: 'Konfiguration',
					description:
						'CommApp-Archivausschnitt auswählen, bevor Erstellung oder Analyse ausgeführt wird.',
					fields: {
						pcName: 'PC-Name',
						year: 'Jahr',
						month: 'Monat',
						part: 'Teil',
					},
					actions: {
						createCommappZip: 'CommApp-Zip erstellen',
					},
				},
				operationLog: {
					title: 'Aktionsprotokoll',
					badge: 'Ergebnisse',
					description: 'Aktuelle Antworten der Admin-Tools.',
				},
				ipcalls: {
					table: {
						ip: 'IP',
						country: 'Land',
						callCount: 'Anzahl Aufrufe',
						created: 'Erstellt',
						modified: 'Aktualisiert',
						approved: 'Freigabe',
					},
					actions: {
						allow: 'freigeben',
						block: 'sperren',
						confirmTitle: 'Sind Sie sicher?',
						allowText: 'IP {ip} erlauben',
						blockText: 'IP {ip} sperren',
					},
					toasts: {
						ipAllowed: 'IP freigegeben',
						ipBlocked: 'IP blockiert',
						listLoaded: 'IP-Liste geladen',
					},
				},
				yocto: {
					status: {
						online: 'online',
						offline: 'offline',
						notConnected: 'Keine Websocket-Verbindung - Seite neu laden',
					},
					sections: {
						futureSettings: 'Geplante Einstellungen',
						onlineSettings: 'Live-Einstellungen',
						currentSensors: 'Aktuelle Sensoren',
						devices: 'Geräte',
						sensors: 'Sensoren (YellowPages)',
						devLogs: 'Entwickler-Logs',
					},
					labels: {
						logicalName: 'Logischer Name',
						luminosity: 'Helligkeit',
						goToSleepAfter: 'Schlafen nach',
						wakeUpSchedule1: 'Aufwachplan 1',
						wakeUpSchedule2: 'Aufwachplan 2',
						sendModuleToSleep: 'Modul in Schlafmodus versetzen',
						disconnectWebsocket: 'Websocket trennen',
					},
					buttons: {
						setSchedule: 'Zeitplan setzen',
						sendFutureSettings: 'Geplante Einstellungen senden',
						sendSleep: 'Schlafbefehl senden',
						disconnect: 'trennen',
						toggleDevLogs: 'Debug-Logs',
					},
				},
			},
		},
	},
}
